import {
  CascadingFilterInput,
  CascadingFilterInputConfigItem,
  CascadingFilterInputOption,
} from '@wrisk/ui-components'
import { isNil, negate, uniq } from 'lodash-es'
import React, { FunctionComponent, useEffect } from 'react'

import {
  toVehicleEngine,
  toVehicleEnginePowerVIN,
  toVehicleManufacturePeriodVIN,
  toVehicleYearRangeVIN,
  VehicleVIN,
} from '../../../../../domain'
import { useApiErrorHandlingAsync } from '../../../../../hooks/auth'
import { usePrincipal } from '../../../../authentication'

interface VehicleResultsFilterProps {
  onSelect: (specification?: VehicleVIN) => void
  initialValue?: VehicleVIN
  profile?: string
  year: number
  make: string
  model: string
}

export const VehicleResultsFilter: FunctionComponent<VehicleResultsFilterProps> = ({
  onSelect,
  profile,
  year,
  make,
  model,
  initialValue,
}) => {
  const { apiClient } = usePrincipal()

  const { result } = useApiErrorHandlingAsync(
    async () =>
      await apiClient.lookupVehicles(
        {
          year,
          make,
          model,
        },
        profile,
      ),
    [],
  )

  useEffect(() => {
    if (result?.length === 1) {
      onSelect(result[0])
    }
  }, [result, onSelect])

  return result ? (
    <CascadingFilterInput
      value={initialValue}
      items={result}
      onSelect={onSelect}
      config={config}
      errorMessage={
        'We couldn’t determine the insurance group for your vehicle. Please get in touch via chat so we can fix this.'
      }
    />
  ) : null
}

const createConfig = (
  id: string,
  placeholder: string,
  expression: (vehicle: VehicleVIN) => string | null | undefined,
  comparator: (a: string, b: string) => number = (a, b) => a.localeCompare(b),
): CascadingFilterInputConfigItem<VehicleVIN> => ({
  id,
  placeholder,
  mapItemsToOptions: (vehicles: VehicleVIN[]) => {
    const attributes = uniq(vehicles.map(expression))
    if (attributes.some(isNil)) {
      return []
    }
    return attributes
      .filter(negate(isNil))
      .sort(comparator)
      .map((id: string) => ({ id, text: id }))
  },
  matchItemToOption:
    ({ id }: CascadingFilterInputOption) =>
    (vehicle: VehicleVIN) =>
      expression(vehicle) === id,
  matchOptionToItem:
    (vehicle: VehicleVIN) =>
    ({ id }: CascadingFilterInputOption) =>
      expression(vehicle) === id,
})

const config = [
  createConfig('vehicle-fuel', 'Fuel', (vehicle) => vehicle.primaryFuel),
  createConfig('vehicle-body', 'Body style', (vehicle) => vehicle.bodyStyle),
  createConfig(
    'vehicle-trim',
    'Trim',
    (vehicle) => vehicle.trim ?? 'None of the above',
    (a, b) => (b === 'None of the above' ? -1 : a.localeCompare(b)),
  ),
  createConfig('vehicle-transmission', 'Transmission', (vehicle) => vehicle.transmission),
  createConfig('vehicle-drive-chain', 'Drive train', (vehicle) => vehicle.driveTrain),
  createConfig('vehicle-engine', 'Engine', toVehicleEngine),
  createConfig('vehicle-power', 'Engine power', toVehicleEnginePowerVIN),
  createConfig('vehicle-seats', 'Number of seats', (vehicle) =>
    vehicle.numberOfSeats?.toString(),
  ),
  createConfig('vehicle-year-range', 'Year range', toVehicleYearRangeVIN, (a, b) =>
    b.localeCompare(a),
  ),
  createConfig(
    'vehicle-secondary-fuel',
    'Secondary fuel',
    (vehicle) => vehicle.secondaryFuel ?? 'None',
  ),
  createConfig(
    'vehicle-electric-type',
    'Electric vehicle type',
    (vehicle) => vehicle.electricVehicleType ?? 'None',
  ),
  createConfig(
    'vehicle-manufacture-period',
    'Manufacture period',
    toVehicleManufacturePeriodVIN,
  ),
  createConfig('vehicle-aspiration', 'Aspiration', (vehicle) => vehicle.aspirationType),
]
